/*
 * Neyli – Suivi de commande : logique d'affichage.
 * La source de données est window.NeyliSuiviData.lookup (voir neyli-suivi-commande-data.js).
 */
if (!customElements.get('neyli-order-tracking')) {
  const ETAPES = ['recue', 'preparation', 'expediee', 'livraison', 'livree'];
  const LIBELLES = {
    recue: 'Commande reçue',
    preparation: 'Commande en préparation',
    expediee: 'Commande expédiée',
    livraison: 'Commande en cours de livraison',
    livree: 'Commande livrée',
  };
  const FORMAT_NUMERO = /^#?\s*[A-Za-z]{0,4}-?\d{3,10}$/;

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  class NeyliOrderTracking extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-tracking-form]');
      this.input = this.querySelector('[data-tracking-input]');
      this.button = this.querySelector('[data-tracking-button]');
      this.message = this.querySelector('[data-tracking-message]');
      this.messageText = this.querySelector('[data-tracking-message-text]');
      this.result = this.querySelector('[data-tracking-result]');
      this.steps = this.querySelectorAll('[data-step]');

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.track();
      });
      this.input.addEventListener('input', () => {
        if (!this.message.hidden) this.hideMessage();
      });
    }

    async track() {
      const numero = this.input.value.trim();

      if (!numero) {
        this.hideResult();
        this.showMessage('Veuillez saisir votre numéro de commande pour suivre son avancement.');
        this.input.focus();
        return;
      }

      if (!FORMAT_NUMERO.test(numero)) {
        this.hideResult();
        this.showMessage('Ce numéro de commande ne semble pas valide. Vérifiez-le dans votre e-mail de confirmation (ex. #1001).');
        return;
      }

      const source = window.NeyliSuiviData;
      if (!source || typeof source.lookup !== 'function') {
        this.showMessage('Le suivi de commande est momentanément indisponible. Merci de réessayer plus tard.');
        return;
      }

      this.hideMessage();
      this.setLoading(true);
      try {
        const commande = await source.lookup(numero);
        if (!commande) {
          this.hideResult();
          this.showMessage('Nous ne trouvons aucune commande avec ce numéro. Vérifiez-le dans votre e-mail de confirmation, ou contactez-nous.');
          return;
        }
        this.render(commande);
      } catch (err) {
        this.hideResult();
        this.showMessage('Une erreur est survenue lors de la recherche. Merci de réessayer dans quelques instants.');
      } finally {
        this.setLoading(false);
      }
    }

    render(c) {
      const indexActuel = Math.max(0, ETAPES.indexOf(c.etape));
      const set = (field, value) => {
        const el = this.result.querySelector(`[data-field="${field}"]`);
        if (el) el.textContent = value || '';
      };
      const toggleMeta = (key, value) => {
        const el = this.result.querySelector(`[data-meta="${key}"]`);
        if (el) el.hidden = !value;
      };

      set('numero', c.numero);
      set('statut', LIBELLES[ETAPES[indexActuel]]);
      set('detail', c.detail);
      set('date', formatDate(c.date));
      set('livraison', formatDate(c.livraisonEstimee));
      set('transporteur', c.transporteur);
      toggleMeta('date', c.date);
      toggleMeta('livraison', c.livraisonEstimee && c.etape !== 'livree');
      toggleMeta('transporteur', c.transporteur);

      this.steps.forEach((li, i) => {
        li.classList.toggle('is-done', i < indexActuel || (i === indexActuel && c.etape === 'livree'));
        li.classList.toggle('is-current', i === indexActuel);
        li.classList.toggle('is-upcoming', i > indexActuel);
        if (i === indexActuel) li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');

        const date = c.dates ? c.dates[ETAPES[i]] : '';
        li.querySelector('[data-step-date]').textContent = i <= indexActuel ? formatDate(date) : '';
        li.querySelector('[data-step-state]').textContent =
          i < indexActuel ? ' (terminée)' : i === indexActuel ? ' (étape actuelle)' : ' (à venir)';
      });

      this.result.style.setProperty('--neyli-progress', indexActuel / (ETAPES.length - 1));
      this.result.hidden = false;
      // Relance la transition d'apparition
      this.result.classList.remove('is-visible');
      requestAnimationFrame(() => requestAnimationFrame(() => this.result.classList.add('is-visible')));
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading;
      this.button.classList.toggle('is-loading', isLoading);
      this.button.setAttribute('aria-busy', String(isLoading));
    }

    showMessage(text) {
      this.messageText.textContent = text;
      this.message.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    hideMessage() {
      this.message.hidden = true;
      this.input.removeAttribute('aria-invalid');
    }

    hideResult() {
      this.result.hidden = true;
      this.result.classList.remove('is-visible');
    }
  }

  customElements.define('neyli-order-tracking', NeyliOrderTracking);
}
